import {Component} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import Logger from './utils/logger.js';

const MODULE = 'NotificationTextPositioner';

/**
 * NotificationTextPositioner
 * 
 * Adjusts notification text position based on viewport aspect ratio.
 * 
 * The text is positioned at a fixed 3D coordinate, but perspective projection
 * causes different screen heights depending on viewport aspect ratio. This component
 * adjusts the Y and Z positions to keep the text at a consistent screen-space 
 * position and size.
 * 
 * Y Formula:
 * - Base Y position (square viewport): -0.6
 * - Adjusted Y: baseY * (1 / aspectRatio) 
 * - This scales Y inversely with aspect ratio, so wider viewports move text up
 * - Bounds: clamped to [-0.8, -0.3] to keep readable and visible in VR
 * 
 * Z Formula:
 * - Base Z position (square viewport): -1.39
 * - Adjusted Z: baseZ * aspectRatio (for aspect > 1.0)
 * - Wider aspect ratios push text further away to maintain apparent size
 * - Bounds: clamped to [-1.7, -1.2] to prevent text from being too far/close
 */
export class NotificationTextPositioner extends Component {
    static TypeName = 'NotificationTextPositioner';

    private boundAdjustNotificationPosition = this.adjustNotificationPosition.bind(this);
    private lastAspectRatio: number | null = null;

    /* Properties that are configurable in the editor */

    @property.bool(false)
    placeTopRight!: boolean;

    @property.bool(false)
    placeTopLeft!: boolean;

    @property.float(0.95)
    baseX!: number;

    @property.float(-0.6)
    baseY!: number;

    @property.float(-1.3912241)
    baseZ!: number;

    /** Extra upward shift (positive = up) applied only in VR mode for the
     *  bottom-centered notification. Has no effect in non-VR mode. */
    @property.float(0.4)
    vrYShift!: number;

    /** Extra horizontal shift applied only in VR mode for top-left anchored
     *  overlays. Negative values move further left, positive values move
     *  right. */
    @property.float(0)
    vrTopLeftXShift!: number;

    start() {
        // Initialize notification position adjustment based on viewport
        this.adjustNotificationPosition();
        window.addEventListener('resize', this.boundAdjustNotificationPosition);
    }

    update(): void {
        const aspectRatio = this.getAspectRatio();
        if (this.lastAspectRatio === null || Math.abs(aspectRatio - this.lastAspectRatio) > 0.01) {
            this.adjustNotificationPosition();
        }
    }

    onActivate(): void {
        this.adjustNotificationPosition();
    }

    public refreshPosition(): void {
        this.adjustNotificationPosition();
    }

    /**
     * Adjust notification text position based on viewport aspect ratio.
     */
    private adjustNotificationPosition(): void {
        const aspectRatio = this.getAspectRatio();
        this.lastAspectRatio = aspectRatio;

        // Base aspect ratio (works well at 1:1)
        const baseAspectRatio = 1.0;

        // For top-anchored overlays, preserve placement near 2:1, move upward
        // as the viewport approaches 1:1, and move slightly downward for wider windows.
        let adjustedY = this.baseY;

        if (this.placeTopRight || this.placeTopLeft) {
            const referenceTopAspect = 2.0;
            const safeAspect = Math.max(0.1, aspectRatio);
            const topScale = Math.max(0.8, Math.min(1.25, referenceTopAspect / safeAspect));
            adjustedY = this.baseY * topScale;
            const minTopY = 0.35;
            const maxTopY = 0.75;
            adjustedY = Math.max(minTopY, Math.min(maxTopY, adjustedY));
        } else {
            // Legacy behavior for bottom-centered notifications.
            const aspectRatioFactor = baseAspectRatio / aspectRatio;
            adjustedY = this.baseY * aspectRatioFactor;
            const minY = -0.8;
            const maxY = -0.3;
            adjustedY = Math.max(minY, Math.min(maxY, adjustedY));
        }

        // Adjust Z proportionally to aspect ratio for wider viewports
        // Wider aspect ratios (> 1.0) will make Z more negative (push text away)
        // This prevents text from appearing too large in widescreen
        let adjustedZ = this.baseZ;
        if (aspectRatio > 1.0) {
            // Scale Z by aspect ratio, but only for widths beyond 1:1
            const zScaleFactor = 1.0 + (aspectRatio - 1.0) * 0.3; // 30% depth increase per unit aspect
            adjustedZ = this.baseZ * zScaleFactor;
        }

        // Clamp Z to reasonable bounds
        // More negative = further away, less negative = closer
        const minZ = -1.7;  // Don't push too far away (text becomes too small)
        const maxZ = -1.2;  // Don't bring too close (text becomes too large)
        adjustedZ = Math.max(minZ, Math.min(maxZ, adjustedZ));

        // Get current position and update X/Y/Z
        const currentPos = this.object.getPositionLocal();
        let adjustedX = currentPos[0];

        if (this.placeTopRight) {
            const xScaleFactor = 1.0 + (Math.max(1.0, aspectRatio) - 1.0) * 0.5;
            adjustedX = this.baseX * xScaleFactor;
            const minX = 0.45;
            const maxX = 1.5;
            adjustedX = Math.max(minX, Math.min(maxX, adjustedX));
        } else if (this.placeTopLeft) {
            const clampedAspect = Math.max(1.0, aspectRatio);
            const baseScale = 1.0 + (clampedAspect - 1.0) * 0.5;
            const wideAspectCompensation = Math.max(0, clampedAspect - 1.6) * 0.7;
            const xScaleFactor = Math.max(1.0, baseScale - wideAspectCompensation);
            adjustedX = this.baseX * xScaleFactor;
            const minX = -1.5;
            const maxX = -0.45;
            adjustedX = Math.max(minX, Math.min(maxX, adjustedX));
        }

        if (this.engine.xr && this.engine.xr.session && this.placeTopLeft) {
            const xrRightShift = 0.6;
            const xrUpShift = .6;
            adjustedX = Math.min(-0.25, adjustedX + xrRightShift + this.vrTopLeftXShift);
            adjustedX = Math.max(-1.5, adjustedX);
            adjustedY = Math.min(1.5, adjustedY + xrUpShift);
        }

        // For the default bottom-centered notification, apply an additional
        // upward shift in VR. The clamp above uses non-VR viewport math so in
        // XR the text tends to sit too low. vrYShift is editor-configurable and
        // has no effect outside of an active XR session.
        if (this.engine.xr && this.engine.xr.session && !this.placeTopLeft && !this.placeTopRight) {
            adjustedY += this.vrYShift;
        }

        this.object.setPositionLocal([adjustedX, adjustedY, adjustedZ]);

        Logger.debug(
            `Adjusted notification position: aspect=${aspectRatio.toFixed(2)}, Y=${adjustedY.toFixed(3)}, Z=${adjustedZ.toFixed(3)}`,
            MODULE
        );
    }

    private getAspectRatio(): number {
        const canvas = this.engine.canvas;
        const canvasWidth = canvas?.width ?? 0;
        const canvasHeight = canvas?.height ?? 0;

        if (canvasWidth > 0 && canvasHeight > 0) {
            let effectiveWidth = canvasWidth;
            if (this.engine.xr && this.engine.xr.session) {
                effectiveWidth = canvasWidth * 0.5;
            }
            return effectiveWidth / canvasHeight;
        }

        const width = window.innerWidth;
        const height = window.innerHeight;
        if (width > 0 && height > 0) {
            return width / height;
        }

        return 1;
    }

    onDestroy(): void {
        // Clean up resize listener
        window.removeEventListener('resize', this.boundAdjustNotificationPosition);
    }
}
